// eslint-disable-next-line import/no-unresolved
import brand from '@brand/brand.js';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog, DialogClose, DialogContent, DialogHeading } from './dialog';

function SpecialistCardBase({ value, title, children }) {
  const { field } = useController({ name: 'specialistId' });
  const selectedValue = useWatch({ name: 'specialistId' });
  const isSelected = selectedValue === value;

  return (
    <div
      className="group relative mb-2.5 p-3.5 rounded-lg border overflow-hidden cursor-pointer transition-colors bg-white border-300 last:mb-0 hover:border-primary data-[selected=true]:bg-primary-50 data-[selected=true]:border-primary group-data-[single-specialist=true]/specialists:data-[value=any]:hidden"
      data-selected={isSelected}
      data-value={value}
    >
      <label
        className="absolute z-10 inset-0 cursor-pointer rounded-lg"
        aria-checked={isSelected}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            field.onChange('any');
          }
        }}
      >
        <input type="radio" checked={isSelected} className="sr-only" {...field} value={value} />
        <span className="sr-only">{title}</span>
      </label>
      <div
        className="absolute top-2 right-2 size-6 flex items-center justify-center rounded-full border transition-colors text-primary border-300 bg-white"
        aria-hidden="true"
      >
        <CheckCircleIcon className="shrink-0 size-7.5 opacity-0 transition-opacity group-data-[selected=true]:opacity-100" />
      </div>
      {children}
    </div>
  );
}

export function AnySpecialistCard() {
  const { t } = useTranslation('page.specialist');
  return (
    <SpecialistCardBase value="any" title={t('anySpecialist.title')}>
      <div>
        <h3 className="title-sm">{t('anySpecialist.title')}</h3>
        <p>{t('anySpecialist.text')}</p>
      </div>
    </SpecialistCardBase>
  );
}

export function SpecialistCard({ specialist }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('page.specialist');
  const specialistId = specialist.practitionerId;

  return (
    <SpecialistCardBase value={specialistId} title={t(`specialists.${specialistId}.name`)}>
      <SpecialistIntro url={specialist.practitionerImageUrl} specialistId={specialistId} />

      <div className="group-data-[single-specialist=false]/specialists:hidden">
        <SpecialistText specialistId={specialistId} />
      </div>

      <button
        className="caption relative z-20 ml-27.5 font-medium text-primary hover:text-primary-hover active:text-primary-active group-data-[single-specialist=true]/specialists:hidden"
        type="button"
        onClick={() => setOpen(true)}
      >
        {t('viewProfile')}
      </button>

      <SpecialistDrawer
        menuOpen={open}
        setMenuOpen={setOpen}
        url={specialist.practitionerImageUrl}
        specialistId={specialistId}
      />
    </SpecialistCardBase>
  );
}

function SpecialistText({ specialistId }) {
  const { t } = useTranslation('page.specialist', { keyPrefix: `specialists.${specialistId}` });
  return (
    <Trans i18nKey="profile" t={t} components={{ p: <p />, b: <b />, i: <i /> }} values={brand} />
  );
}

function SpecialistIntro({ specialistId, url }) {
  const { t } = useTranslation('page.specialist', { keyPrefix: `specialists.${specialistId}` });
  return (
    <div className="py-2.5 mb-2.5 flex items-center gap-2.5">
      <img
        className="shrink-0 size-25 object-cover rounded-full overflow-hidden bg-primary-100 group-data-[selected=true]:bg-white"
        src={url}
        alt={t('name')}
      />
      <div>
        <h3 className="title-sm mb-0">{t('name')}</h3>
        <p className="caption mb-1.25">{t('titles')}</p>
        <p className="font-medium">{t('profession')}</p>
      </div>
    </div>
  );
}

export function SpecialistDrawer({ menuOpen, setMenuOpen, specialistId, url }) {
  const { t } = useTranslation();
  const { t: tSpecialist } = useTranslation('page.specialist');

  return (
    <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
      <DialogContent className="Dialog fixed top-0 left-0 size-full overflow-y-auto md:w-[500px] p-5 bg-primary-active text-white">
        <div className="container">
          <DialogHeading className="title-sm flex items-start justify-between gap-4 mb-2.5">
            <span>{tSpecialist('profile')}</span>

            <DialogClose className="hover:text-primary-light" aria-label={t('closeDialog')}>
              <XMarkIcon className="size-6" aria-hidden="true" />
            </DialogClose>
          </DialogHeading>
          <SpecialistIntro isSelected={true} url={url} specialistId={specialistId} />
          <SpecialistText specialistId={specialistId} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
